import cn from 'classnames'
import { modal_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import React, { ReactElement } from 'react'
import Modal, { Props as ModalProps } from 'react-modal'
import styled from 'styled-components'

import CloseButton from '../CloseButton/CloseButton'
import style from './BaseModal.style'

export type BaseModalProps = Omit<ModalProps, 'onRequestClose'> & {
  className?: string
  /** If `true`, this Modal is open */
  isOpen: boolean
  /**
   * Use the `popup` (default) variant for simple tasks like showing the status of downloads
   * or to confirm something. This variant contains styling to help
   *
   * Use the `interactive` variant for forms and larger multi-step processes
   *
   * Use the `fullscreen` variant for immersive, near-full-viewport flows such as
   * multi-step wizards that need maximum room (e.g. bulk imports).
   */
  variant?: 'popup' | 'interactive' | 'fullscreen'
  /** Function to run on close button click */
  onClose: () => void
  /** Shows the close button if `true` */
  showCloseButton?: boolean
  /**
   * Sets the `id` and `data-testid` on the close button. Lets consumers keep
   * stable selectors for tests/automation.
   */
  closeButtonId?: string
  /**
   * Renders the body without its default surface (background, shadow, radius,
   * padding) and drops the variant width, so the child element defines the
   * card. Used by the legacy Popup, which supplies its own white box.
   */
  unstyledBody?: boolean
}

function BaseModal(
  props: BaseModalProps & { 'aria-label': string },
): ReactElement {
  const {
    className = '',
    variant = 'popup',
    isOpen,
    children,
    onClose,
    showCloseButton = true,
    closeButtonId,
    unstyledBody,
    shouldCloseOnEsc,
    shouldCloseOnOverlayClick,
    'aria-label': ariaLabel,
  } = props

  return (
    <Modal
      className={cn('BaseModal-Content', variant, {
        'unstyled-body': unstyledBody,
      })}
      overlayClassName={{
        base: 'BaseModal-Overlay',
        afterOpen: 'BaseModal-Overlay-open',
        beforeClose: 'BaseModal-Overlay-closed',
      }}
      portalClassName={cn('BaseModal', className)}
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      closeTimeoutMS={parseInt(modal_transitionSpeed)}
      bodyOpenClassName="modal-open"
      contentLabel={ariaLabel}
    >
      {showCloseButton && (
        <CloseButton
          className="BaseModal-CloseButton"
          variant="light-on-dark"
          onClick={onClose}
          {...(closeButtonId != null
            ? { id: closeButtonId, 'data-testid': closeButtonId }
            : {})}
        />
      )}
      <div className="BaseModal-Body">{children}</div>
    </Modal>
  )
}

// We have to use `styled` instead of the `css` prop because the `css` prop
// will automatically put the styled-components mangled className in the
// `className` prop on `<Modal>`. However, that puts the class on the Content,
// preventing us from styling the Overlay. By using `styled`, we are able to
// pass the className to `portalClassName`, which will style the parent of both
// Overlay and Content.
export default styled(BaseModal)`
  ${style}
`
